/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import KeyValueTooltip from "components/charts/modern/tooltip/KeyValueTooltip";
import getContourColors from "components/stocks/modern/OperatingSegments/getContourColors";
import aeonik from "components/utils/aeonik";
import type { SeriesOptionsType } from "highcharts";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { renderToStaticMarkup } from "react-dom/server";
import colors from "tailwindcss/colors";

interface AllSegmentsHistoryColumnsChartProps {
  className?: string;
  kind: "GEOGRAPHIC" | "BUSINESS";
  segments: {
    name: string;
    values: {
      x: number;
      y: number;
    }[];
  }[];
}

export default function AllSegmentsHistoryColumnsChart({
  className,
  kind,
  segments,
}: AllSegmentsHistoryColumnsChartProps) {
  const minValues = segments.flatMap((node) => node.values.map((node) => node.y));
  const minValue = minValues.length > 0 ? Math.min(...minValues) : 0;

  const series: SeriesOptionsType[] = segments
    .sort((a, b) => {
      const aMax = Math.max(...a.values.map((node) => node.y));
      const bMax = Math.max(...b.values.map((node) => node.y));
      return aMax - bMax;
    })
    .map((node, index) => {
      const data = node.values
        .sort((a, b) => a.x - b.x)
        .map((value) => {
          return {
            x: value.x,
            y: value.y,
            custom: node,
          };
        });
      const adjustedIndex = segments.length - 1 - index;
      return {
        type: "column",
        stacking: "normal",
        name: node.name,
        color: getContourColors(adjustedIndex).background,
        borderColor: getContourColors(adjustedIndex).text,
        borderWidth: 1.5,
        data,
      };
    });

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      type: "pie",
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: colors.slate[100],
    },
    scrollbar: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series,
    legend: {
      align: "left",
      enabled: true,
      padding: 4,
      alignColumns: true,
      itemDistance: 16,
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: colors.slate[800],
        fontFamily: aeonik.style.fontFamily,
        fontWeight: "500",
        fontSize: "12px",
        width: 120,
        textOverflow: "ellipsis",
      },
      reversed: true,
    },
    xAxis: {
      // minPadding: 0.15,
      // maxPadding: 0.15,
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: colors.slate[100],
      lineWidth: 1,
      lineColor: colors.slate[100],
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        step: 1,
        enabled: true,
        format: "{value}",
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
    },
    yAxis: {
      min: minValue,
      gridLineColor: colors.slate[100],
      lineWidth: 1,
      lineColor: colors.slate[100],
      labels: {
        enabled: true,
        align: "center",
        formatter() {
          if (!this.value || typeof this.value !== "number") {
            return "";
          }
          const moneyFormatter = new Intl.NumberFormat("en-US", {
            notation: "compact",
            compactDisplay: "short",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            minimumSignificantDigits: 1,
          });

          const percentageFormatter = new Intl.NumberFormat("en-US", {
            style: "percent",
            notation: "standard",
            maximumSignificantDigits: 2,
          });

          return kind === "BUSINESS" ? moneyFormatter.format(this.value) : percentageFormatter.format(this.value / 100);
        },
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      tickWidth: 0,
      showFirstLabel: true,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
    },
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        if (!this.key || !this.points || this.points.length === 0) {
          return "";
        }

        const valueFormatter = new Intl.NumberFormat("en", {
          notation: "compact",
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        });
        const keyValues = this.points
          .sort((a, b) => {
            return (b.y || 0) - (a.y || 0);
          })
          .map(({ series, y }) => {
            if (!y) {
              return null;
            }

            return {
              key: series.name,
              value: kind === "BUSINESS" ? valueFormatter.format(y) : `${valueFormatter.format(y)}%`,
              yValue: y,
              color:
                series.userOptions.type === "column" && typeof series.userOptions.borderColor === "string"
                  ? series.userOptions.borderColor
                  : undefined,
            };
          })
          .filter(notUndefinedOrNull);

        return renderToStaticMarkup(<KeyValueTooltip x={this.key} keyValues={keyValues} />);
      },
    },
    plotOptions: {
      column: {
        clip: true,
        borderRadius: {
          where: "end",
          radius: 8,
          scope: "stack",
        },
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
  };

  return <HighchartsChart options={options} className={className} />;
}
